import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, IconButton, MenuItem, Snackbar, Tooltip } from '@mui/material';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import { useQueryClient } from '@tanstack/react-query';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useBulkSendStaging } from '../../../context/BulkSendStagingContext.jsx';
import useApi from '../../../hooks/useApi';
import { scheduleMrtFilterPopoverOpen } from '../../../utils/mrtFilterPopover';
import { getOrderItemAccountId, getOrderItemRowId } from './appsheetFieldUtils';
import { buildAppsheetVmtRecord, getMissingRequiredFields } from './appsheetVmtMapping';
import { useAppsheetOrderItemsTableState } from './appsheetOrderItemsTableState';
import AppSheetOrderItemsToolbar from './AppSheetOrderItemsToolbar';
import AppSheetOrderItemsSendMenu from './AppSheetOrderItemsSendMenu';
import { buildOrderItemsColumns } from './appsheetOrderItemsColumns';
import { exportVisibleCsv } from './appsheetOrderItemsExport';
import { buildOwnedMrtLayout } from '../utils/detailPanelTableLayout';
import VisibleFirstColumnsButton from '../../VisibleFirstColumnsButton';

const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();

// AppSheetOrderItemsTable is the operational grid for AppSheet order items, including Trade CPQ rows when present.
// It combines table state, VMT staging/send affordances, and CSV export around a
// dynamic column set inferred from the cached AppSheet schema.
const AppSheetOrderItemsTable = ({
  rows = [],
  fields = [],
  accountsById,
  userEmail = null,
  onNotify = null,
  onEditAndSendVmt = null,
  summary,
  itemLabel = 'Order item',
  itemLabelPlural = 'order items',
  emptyMessage = 'No AppSheet order items found for this BAC.',
  vmtRecordDefaults = null,
  vmtRecordOverrides = null,
  enableInternalScroll = true,
  minTableHeight = 0,
  hideFullScreenToggle = false,
  tableContainerTestId = null,
}) => {
  const queryClient = useQueryClient();
  const { stageRecord } = useBulkSendStaging();
  const { useVmtSend } = useApi();
  const sendMutation = useVmtSend({
    sendMode: vmtRecordOverrides?.programName === 'Trade' ? 'trade' : 'general',
  });
  const isVitest =
    typeof import.meta !== 'undefined' &&
    import.meta?.env &&
    (import.meta.env.MODE === 'test' || Boolean(import.meta.env.VITEST));

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sendMenu, setSendMenu] = useState({ anchorEl: null, row: null });
  const [notesModal, setNotesModal] = useState({ open: false, row: null, notes: '' });
  const [rowSelection, setRowSelection] = useState({});
  const [pendingFilterColumnId, setPendingFilterColumnId] = useState(null);

  const tableState = useAppsheetOrderItemsTableState(userEmail, { left: ['sendToStaging', 'accountName'] });
  const layoutProps = useMemo(
    () =>
      buildOwnedMrtLayout({
        enableInternalScroll,
        minHeight: minTableHeight,
        internalTableContainerSx: {
          flex: 1,
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'auto',
        },
        internalTablePaperSx: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
        },
      }),
    [enableInternalScroll, minTableHeight]
  );
  const tableContainerProps = useMemo(
    () => ({
      ...(layoutProps.muiTableContainerProps || {}),
      ...(tableContainerTestId ? { 'data-testid': tableContainerTestId } : {}),
    }),
    [layoutProps.muiTableContainerProps, tableContainerTestId]
  );

  const data = useMemo(
    () =>
      (rows || []).map((row, index) => ({
        __rowId: getOrderItemRowId(row, index),
        ...row,
      })),
    [rows]
  );

  const resolveAccountName = useCallback(
    (row) => {
      const accountId = getOrderItemAccountId(row);
      if (!accountId) return '';
      const key = normalizeKey(accountId);
      return accountsById?.get(key)?.name || '';
    },
    [accountsById]
  );

  const handleSendToStaging = useCallback(
    (row, notes = '', event = null) => {
      if (event) event.stopPropagation();
      if (!row) return;
      const record = buildAppsheetVmtRecord(row, {
        defaults: vmtRecordDefaults,
        overrides: vmtRecordOverrides,
      });
      const trimmedNote = notes.trim();
      if (trimmedNote) record.notes = trimmedNote;
      stageRecord(record, { dealerName: resolveAccountName(row), brands: record.gmBrandCd || '' });
      setSnackbar({
        open: true,
        message: `${itemLabel} added to staging${trimmedNote ? ' with notes' : ''}.`,
        severity: 'success',
      });
      onNotify?.(`${itemLabel} staged.`, 'success');
    },
    [itemLabel, onNotify, resolveAccountName, stageRecord, vmtRecordDefaults, vmtRecordOverrides]
  );

  const handleSendToVmt = useCallback(
    async (row) => {
      if (!row) return;
      try {
        // Direct-send stamps last-updated metadata so the outbound VMT payload
        // reflects that it was sent from this AppSheet-driven operator action.
        const record = buildAppsheetVmtRecord(row, {
          stampLastUpdatedDate: true,
          defaults: vmtRecordDefaults,
          overrides: vmtRecordOverrides,
        });
        await sendMutation.mutateAsync({ records: [record], userEmail });
        queryClient.invalidateQueries({ queryKey: ['vmtSendHistory'] });
        setSnackbar({ open: true, message: `${itemLabel} sent to VMT.`, severity: 'success' });
        onNotify?.(`${itemLabel} sent to VMT.`, 'success');
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || error?.message || 'Failed to send to VMT.',
          severity: 'error',
        });
      }
    },
    [itemLabel, onNotify, queryClient, sendMutation, userEmail, vmtRecordDefaults, vmtRecordOverrides]
  );

  const handleExportVisibleCsv = useCallback(
    (tableInstance) =>
      exportVisibleCsv({
        table: tableInstance,
        onEmpty: () => setSnackbar({ open: true, message: 'No rows to export.', severity: 'info' }),
        onSuccess: (count) => setSnackbar({ open: true, message: `Exported ${count} row(s).`, severity: 'success' }),
        onError: () => setSnackbar({ open: true, message: 'Failed to export CSV.', severity: 'error' }),
      }),
    []
  );

  const columns = useMemo(
    () =>
      buildOrderItemsColumns({
        fields,
        resolveAccountName,
        onSendToStaging: handleSendToStaging,
        onOpenMenu: (anchorEl, row) => setSendMenu({ anchorEl, row }),
        itemLabel,
      }),
    [fields, handleSendToStaging, itemLabel, resolveAccountName]
  );

  const handleResetFilters = useCallback(
    (tableInstance) => {
      try {
        tableInstance?.setColumnFilters?.([]);
        tableInstance?.setGlobalFilter?.('');
        tableInstance?.resetColumnFilters?.();
      } catch (_err) {
        // noop
      }
    },
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    localization: MRT_Localization_EN,
    getRowId: (row) => getOrderItemRowId(row),
    enableRowSelection: true,
    enableSelectAll: true,
    enableRowVirtualization: enableInternalScroll ? !isVitest : false,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnOrdering: true,
    enableColumnDragging: false,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableSorting: true,
    enablePagination: true,
    enableBottomToolbar: true,
    enableFullScreenToggle: !hideFullScreenToggle,
    state: { ...tableState.state, rowSelection },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: tableState.handlers.setColumnVisibility,
    onColumnOrderChange: tableState.handlers.setColumnOrder,
    onColumnPinningChange: tableState.handlers.setColumnPinning,
    onGlobalFilterChange: tableState.handlers.setGlobalFilter,
    onColumnFiltersChange: tableState.handlers.setColumnFilters,
    onSortingChange: tableState.handlers.setSorting,
    initialState: {
      ...tableState.initialState,
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <AppSheetOrderItemsToolbar
        summary={summary}
        onExport={() => handleExportVisibleCsv(table)}
        itemLabel={String(itemLabel || 'Order item').toLowerCase()}
        itemLabelPlural={itemLabelPlural}
      />
    ),
    renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table }) => {
      const canFilter = column.getCanFilter?.();
      if (!canFilter) return internalColumnMenuItems;

      const addFilterItem = (
        <MenuItem
          key="add-filter"
          onClick={() => {
            setPendingFilterColumnId(column.id);
            closeMenu?.();
          }}
        >
          <FilterAltIcon fontSize="small" style={{ marginRight: 8 }} />
          Add Filter
        </MenuItem>
      );

      const internalItems = Array.isArray(internalColumnMenuItems)
        ? [...internalColumnMenuItems]
        : internalColumnMenuItems
          ? [internalColumnMenuItems]
          : [];

      const getMenuItemLabel = (node) => {
        if (!node) return '';
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(getMenuItemLabel).join('');
        if (typeof node === 'object') {
          if (node.props?.label) return node.props.label;
          if (node.props?.children) return getMenuItemLabel(node.props.children);
        }
        return '';
      };

      const clearFilterLabel = table?.options?.localization?.clearFilter || 'Clear Filter';
      const normalizedClearFilterLabel = clearFilterLabel
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      const clearFilterIndex = internalItems.findIndex((item) => {
        const label = getMenuItemLabel(item);
        const normalizedLabel = label
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
        return normalizedLabel === normalizedClearFilterLabel;
      });

      if (clearFilterIndex === -1) {
        return [addFilterItem, ...internalItems];
      }
      internalItems.splice(clearFilterIndex, 0, addFilterItem);
      return internalItems;
    },
    muiTableHeadCellProps: ({ column }) => {
      const isSorted = Boolean(column.getIsSorted?.());
      return {
        sx: {
          whiteSpace: 'nowrap',
          '& .Mui-TableHeadCell-Content button[aria-label*="Filter by"]': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '& .Mui-TableHeadCell-Content-Labels': {
            flexWrap: 'nowrap',
          },
          '& .Mui-TableHeadCell-Content-Wrapper': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root': {
            display: 'inline-flex',
          },
          '& .MuiTableSortLabel-root': {
            display: isSorted ? 'inline-flex' : 'none',
          },
        },
      };
    },
    renderToolbarInternalActions: ({ table }) => {
      const {
        columnFilterDisplayMode,
        enableColumnFilters,
        enableColumnOrdering,
        enableColumnPinning,
        enableDensityToggle,
        enableFilters,
        enableFullScreenToggle,
        enableGlobalFilter,
        enableHiding,
        initialState,
      } = table.options;
      const { columnFilters: activeColumnFilters, globalFilter: activeGlobalFilter } = table.getState();
      const hasActiveFilters = Boolean(activeGlobalFilter) || (activeColumnFilters?.length || 0) > 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {enableFilters && enableGlobalFilter && !(initialState?.showGlobalFilter) ? (
            <MRT_ToggleGlobalFilterButton table={table} />
          ) : null}
          <Tooltip title="Reset filters">
            <span>
              <IconButton size="small" onClick={() => handleResetFilters(table)} disabled={!hasActiveFilters}>
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {enableFilters && enableColumnFilters && columnFilterDisplayMode !== 'popover' ? (
            <MRT_ToggleFiltersButton table={table} />
          ) : null}
          {(enableHiding || enableColumnOrdering || enableColumnPinning) ? (
            <VisibleFirstColumnsButton table={table} />
          ) : null}
          {enableDensityToggle ? <MRT_ToggleDensePaddingButton table={table} /> : null}
          {enableFullScreenToggle ? <MRT_ToggleFullScreenButton table={table} /> : null}
        </Box>
      );
    },
    muiTableContainerProps: tableContainerProps,
    muiTablePaperProps: layoutProps.muiTablePaperProps,
    muiTopToolbarProps: layoutProps.muiTopToolbarProps,
    muiBottomToolbarProps: layoutProps.muiBottomToolbarProps,
  });

  React.useEffect(() => {
    if (!pendingFilterColumnId || !table) return;
    return scheduleMrtFilterPopoverOpen({
      table,
      columnId: pendingFilterColumnId,
      buttonSelector: 'button[aria-label*="Filter by"]',
      onComplete: () => setPendingFilterColumnId(null),
    });
  }, [pendingFilterColumnId, table]);

  const handleSendMenuClose = useCallback(() => setSendMenu({ anchorEl: null, row: null }), []);
  const handleMenuSendWithNotes = useCallback(
    (row) => {
      handleSendMenuClose();
      if (!row) return;
      setNotesModal({ open: true, row, notes: '' });
    },
    [handleSendMenuClose]
  );

  const missingFields = sendMenu.row
    ? getMissingRequiredFields(sendMenu.row, {
        defaults: vmtRecordDefaults,
        overrides: vmtRecordOverrides,
      })
    : [];
  const vmtDisabled = missingFields.length > 0;
  const vmtTooltip = vmtDisabled ? `Missing required fields: ${missingFields.join(', ')}` : 'Send to VMT';

  return (
    <>
      {data.length === 0 ? (
        <Alert severity="info">{emptyMessage}</Alert>
      ) : (
        <Box
          sx={
            enableInternalScroll
              ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
              : { flex: 1, minHeight: minTableHeight, display: 'flex', flexDirection: 'column' }
          }
        >
          <MaterialReactTable table={table} />
        </Box>
      )}

      <AppSheetOrderItemsSendMenu
        anchorEl={sendMenu.anchorEl}
        row={sendMenu.row}
        onClose={handleSendMenuClose}
        onSendToStaging={handleSendToStaging}
        onSendWithNotes={handleMenuSendWithNotes}
        onSendToVmt={handleSendToVmt}
        onEditAndSendVmt={onEditAndSendVmt}
        vmtDisabled={vmtDisabled}
        vmtTooltip={vmtTooltip}
        notesModal={notesModal}
        setNotesModal={setNotesModal}
        itemLabel={itemLabel}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AppSheetOrderItemsTable;
